/**
 * SeamCrossingRushPanel — arcade game for witnessed seam crossings.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { CROSSING_WAVES } from "../data/crossingWaves.js";
import {
  activeOf,
  arcadeEvent,
  crossingConstructable,
  initialState,
  paletteOf,
  reducer,
  waveByIndex,
  waveCleared,
  type RushIntent,
  type RushState,
} from "../algorithms/seamCrossingRush.js";
import {
  ArcadeActionButton,
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeBlockedSlot,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  ArcadeStatusDots,
  arcadeRankColor,
  createArcadeHudStripProps,
  pointsForHit,
  rankForScore,
  timeBonus,
  useArcadeClock,
  useArcadeHotkeys,
  useReducedMotion,
} from "../../../games/index.js";
import {
  ROUND_SECONDS,
  TICK_MS,
} from "./seamCrossingPresentation.js";
import { ApproachingSeam, WitnessPalette } from "./SeamCrossingRushBoard.js";
export function SeamCrossingRushPanel(): React.JSX.Element {
  const [state, dispatch] = useReducer(
    (prev: RushState, intent: RushIntent) =>
      reducer(CROSSING_WAVES, prev, intent),
    CROSSING_WAVES,
    initialState,
  );
  const reducedMotion = useReducedMotion();
  const [combo, setCombo] = useState(0);
  const [arcadeScore, setArcadeScore] = useState(0);
  const [flashToken, setFlashToken] = useState(0);
  const [flashColor, setFlashColor] = useState(panelTokens.accentSafe);
  const [roundOver, setRoundOver] = useState(false);
  const clock = useArcadeClock(ROUND_SECONDS, () => {
    setCombo(0);
    setRoundOver(true);
  });
  const prevStateRef = useRef(state);
  const clockRemainingRef = useRef(clock.remaining);
  clockRemainingRef.current = clock.remaining;
  const pauseClock = clock.pause;
  const startClock = clock.start;
  const resetClock = clock.reset;
  const clockRunning = clock.running;
  const cleared = waveCleared(CROSSING_WAVES, state);
  const wave = waveByIndex(CROSSING_WAVES, state.waveIndex);
  const palette = paletteOf(CROSSING_WAVES, state);
  const active = activeOf(state);
  const accent = panelTokens.accentGold;
  const rank = rankForScore(arcadeScore);
  const isLast = state.waveIndex === CROSSING_WAVES.length - 1;
  const activeConstructable = active !== null && crossingConstructable(active);
  useEffect(() => {
    startClock();
  }, [startClock]);
  useEffect(() => {
    const prev = prevStateRef.current;
    if (prev === state) return;
    const event = arcadeEvent(prev, state);
    switch (event) {
      case "cross": {
        const nextCombo = combo + 1;
        setCombo(nextCombo);
        setArcadeScore(
          (s) =>
            s +
            pointsForHit(nextCombo) +
            (roundOver ? 0 : timeBonus(clockRemainingRef.current)),
        );
        setFlashColor(panelTokens.accentSafe);
        setFlashToken((t) => t + 1);
        break;
      }
      case "refuse": {
        setCombo(0);
        setFlashColor(panelTokens.accentDanger);
        setFlashToken((t) => t + 1);
        break;
      }
      case "miss": {
        setCombo(0);
        setFlashColor(panelTokens.accentAmber);
        setFlashToken((t) => t + 1);
        break;
      }
      case "none":
        break;
    }
    prevStateRef.current = state;
  }, [state, combo, roundOver]);
  useEffect(() => {
    if (!clockRunning || roundOver || cleared) return;
    const handle = window.setInterval(() => {
      dispatch({ kind: "tick" });
    }, TICK_MS);
    return (): void => {
      window.clearInterval(handle);
    };
  }, [clockRunning, roundOver, cleared]);
  useEffect(() => {
    if (cleared && !roundOver) {
      setRoundOver(true);
      pauseClock();
    }
  }, [cleared, roundOver, pauseClock]);
  const play = useCallback((intent: RushIntent): void => {
    dispatch(intent);
  }, []);
  const beginRound = useCallback((): void => {
    setCombo(0);
    setRoundOver(false);
    setFlashToken(0);
    resetClock();
    startClock();
  }, [resetClock, startClock]);
  const retryRound = useCallback((): void => {
    beginRound();
    dispatch({ kind: "reset" });
  }, [beginRound]);
  const advanceWave = useCallback((): void => {
    beginRound();
    dispatch({ kind: "next" });
  }, [beginRound]);
  useArcadeHotkeys([
    {
      code: "Space",
      enabled: true,
      onTrigger: (): void => {
        if (active === null) return;
        play({ kind: "dispatch" });
      },
    },
    {
      key: "Enter",
      enabled: active !== null,
      onTrigger: (): void => {
        play({ kind: "dispatch" });
      },
    },
    {
      key: "n",
      enabled: cleared && !isLast,
      onTrigger: advanceWave,
    },
    {
      key: "r",
      onTrigger: retryRound,
    },
    ...palette.slice(0, 9).map((component, index) => ({
      key: String(index + 1),
      enabled: active !== null,
      onTrigger: (): void => {
        if (active === null) return;
        const held = active.attached.includes(component.id);
        play({
          kind: held ? "detach" : "attach",
          witnessId: component.id,
        });
      },
    })),
  ]);
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      if (active === null) return;
      play({ kind: "dispatch" });
      return;
    }
    const digit = Number.parseInt(event.key, 10);
    if (!Number.isNaN(digit) && digit >= 1 && digit <= palette.length) {
      event.preventDefault();
      event.stopPropagation();
      const component = palette.at(digit - 1);
      if (component === undefined || active === null) return;
      const held = active.attached.includes(component.id);
      play({
        kind: held ? "detach" : "attach",
        witnessId: component.id,
      });
    }
  }
  if (wave === null) {
    return (
      <PanelSurface.PanelShell
        title="Seam Crossing Rush"
        subtitle="No waves loaded."
        accentColor={panelTokens.accentGold}
      >
        <PanelSurface.Card>No crossing waves are available.</PanelSurface.Card>
      </PanelSurface.PanelShell>
    );
  }
  return (
    <PanelSurface.PanelShell
      title="Seam Crossing Rush"
      subtitle="Assemble each crossing's required witness tuple before it reaches the seam."
      accentColor={accent}
    >
      <ArcadeKeyframes />

      <PanelSurface.SectionHeading>
        Seam Arena · {wave.title}
      </PanelSurface.SectionHeading>
      <ArcadeArenaCard>
        <ArcadeArenaStage
          hudProps={createArcadeHudStripProps({
            stats: [
              {
                label: "crossed",
                value: `${state.crossed}/${wave.quota}`,
                color: panelTokens.accentSafe,
              },
              {
                label: "refused",
                value: state.refused,
                color: panelTokens.accentDanger,
              },
              {
                label: "missed",
                value: state.missed,
                color: panelTokens.accentAmber,
              },
              { label: "wave", value: state.waveIndex + 1, color: accent },
            ],
            score: arcadeScore,
            rank,
            rankColor: arcadeRankColor(rank),
            scoreColor: panelTokens.bright,
            borderColor: panelTokens.border,
            clock,
            timerAccent: accent,
            timerDanger: panelTokens.accentDanger,
            timerTrack: panelTokens.elev,
            combo,
            comboColor: accent,
            comboMuted: panelTokens.muted,
            reducedMotion,
            background: "rgba(0,0,0,0.24)",
          })}
        >
          <ArcadePlayfieldFrame
            borderColor={panelTokens.border}
            background="rgba(7,8,22,0.9)"
            accentColor={accent}
            secondaryColor={panelTokens.accentSafe}
            gridSize={46}
            minHeight={560}
            header={
              <div className="flex flex-wrap items-center justify-between gap-3">
                <PanelSurface.SectionHeading>
                  Witness tuple gate
                </PanelSurface.SectionHeading>
                <PanelSurface.Badge color={panelTokens.muted}>
                  complete tuple or no crossing
                </PanelSurface.Badge>
              </div>
            }
          >
            <ArcadeMissionPrompt
              label="front crossing"
              title={active?.title ?? "Crossing stream warming up"}
              detail={wave.brief}
              color={activeConstructable ? panelTokens.accentSafe : accent}
              hotkey={active === null ? undefined : "Space"}
              status={
                activeConstructable
                  ? "constructable"
                  : active === null
                    ? "waiting"
                    : "needs tuple"
              }
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(220px,0.72fr)]">
              <ApproachingSeam
                state={state}
                palette={palette}
                accent={accent}
                flashToken={flashToken}
                flashColor={flashColor}
                reducedMotion={reducedMotion}
              />
              <WitnessPalette
                palette={palette}
                active={active}
                accent={accent}
                onKeyDown={handleKeyDown}
                onToggle={(component, held) => {
                  play({
                    kind: held ? "detach" : "attach",
                    witnessId: component.id,
                  });
                }}
              />
            </div>
            <ArcadeBlockedSlot
              label="Absent crossing"
              title="Incomplete tuple"
              detail="A missing witness component does not produce a crossing arrow. Complete the tuple and the crossing becomes constructable by construction."
              color={panelTokens.accentDanger}
              className="mt-4"
            />
          </ArcadePlayfieldFrame>

          <ArcadeLogPanel
            title="Crossing log"
            event={logEvent(state.status, roundOver, cleared, rank)}
            borderColor={panelTokens.border}
            background="rgba(7,8,22,0.68)"
            textColor={panelTokens.text}
            badges={[
              PanelSurface.badge("seam", accent),
              PanelSurface.badge("witness tuple", panelTokens.accentSafe),
            ]}
          >
            <PanelSurface.Stack>
              <ArcadeStatusDots
                items={[
                  { label: "front request", active: active !== null },
                  { label: "tuple complete", active: activeConstructable },
                  { label: "wave cleared", active: cleared },
                ]}
                activeColor={panelTokens.accentSafe}
                inactiveColor={panelTokens.muted}
              />
              <div className="grid gap-2">
                <ArcadeActionButton
                  color={activeConstructable ? panelTokens.accentSafe : accent}
                  disabled={active === null}
                  onClick={() => {
                    play({ kind: "dispatch" });
                  }}
                  ariaLabel="Dispatch the front crossing"
                  icon=">"
                >
                  Dispatch
                </ArcadeActionButton>
                <div className="grid grid-cols-2 gap-2">
                  <ArcadeActionButton
                    color={panelTokens.accentAmber}
                    onClick={retryRound}
                    ariaLabel="Retry current wave"
                    icon="R"
                  >
                    Retry
                  </ArcadeActionButton>
                  <ArcadeActionButton
                    color={
                      cleared && !isLast
                        ? panelTokens.accentSafe
                        : panelTokens.muted
                    }
                    disabled={!cleared || isLast}
                    onClick={advanceWave}
                    ariaLabel="Advance to next crossing wave"
                    icon=">"
                  >
                    Next
                  </ArcadeActionButton>
                </div>
              </div>
            </PanelSurface.Stack>
          </ArcadeLogPanel>
        </ArcadeArenaStage>
      </ArcadeArenaCard>

      <PanelSurface.Callout
        title="A crossing is constructable only with its complete witness tuple"
        tone="info"
      >
        A governed seam crossing carries a witness tuple. When the attached set
        exactly equals the required tuple, the crossing arrow exists. Drop any
        required component and the crossing is structurally inexpressible: there
        is no arrow for the agent to take.
      </PanelSurface.Callout>
    </PanelSurface.PanelShell>
  );
}
function logEvent(
  status: string,
  roundOver: boolean,
  cleared: boolean,
  rank: string,
): string {
  if (cleared) {
    return `Wave cleared — rank ${rank}. Every constructed crossing carried its complete witness tuple.`;
  }
  if (roundOver) {
    return `${status} Timer expired: retry the wave or keep assembling without a bonus.`;
  }
  return status;
}
